import dayjs, { type Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { Stack, Typography } from '@mui/material';
import { useField } from 'formik';
import { ReactNode } from 'react';

import CustomTextField from './DateTextField';

/**
 * Formik Wrapper for date inputs
 *
 * @example <DateField label="Test" name="test" inputComponentProps={{ prefix: 'ft' }} />
 *
 * @param {Object} props
 * @param {string} props.name - unique field name in your form
 */
interface DateFieldProps extends DatePickerProps<Dayjs> {
    isIconVisible?: boolean;
    isShowError?: boolean;
    name?: string;
    disableFuture?: boolean;
    defaultError?: string;
    minDate?: any;
    maxDate?: any;
    label?: ReactNode;
    variant?: string;
    TextFieldProps?: Record<string, any>;
    inputFormat?: string;
    disabled?: boolean;
    textOnDisabled?: boolean;
}

function DateField({
    isIconVisible = true,
    isShowError = true,
    defaultError,
    inputFormat = 'MM/DD/YYYY',
    TextFieldProps = {},
    textOnDisabled = false,
    disabled,
    ...props
}: DateFieldProps) {
    const [{ onBlur: onFieldBlur, onChange: onFieldChange, ...field }, meta, { setValue, setTouched }] = useField(
        props as any
    );
    const onChange = async (value: any) => {
        await setValue(value?.format(inputFormat) ?? '');
        setTouched(true, true);
    };

    const isError = isShowError ? !!meta.error && meta.touched : false;
    const error = isError ? defaultError ?? meta.error : null;

    let value = field.value || null;

    if (value) {
        value = dayjs.isDayjs(value) ? value : dayjs(value);
    }

    if (textOnDisabled && disabled) {
        return (
            <Stack>
                <Typography variant="caption">{props.label}</Typography>
                <Typography>{value?.format(inputFormat) || '---'}</Typography>
            </Stack>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
                format={inputFormat}
                disabled={disabled}
                {...field}
                {...props}
                value={value}
                slots={{
                    textField: CustomTextField as any,
                    ...props.slots,
                }}
                slotProps={{
                    ...props.slotProps,
                    textField: {
                        isIconVisible,
                        textFieldProps: TextFieldProps,
                        isError,
                        helperText: error,
                        variant: 'standard',
                        disabled,
                        ...props.slotProps?.textField,
                    } as any,
                }}
                onChange={onChange}
            />
        </LocalizationProvider>
    );
}

export default DateField;
